/**
 * The guest demo's top-level view (AD-794 §6.2; story
 * `q-guest-demo-s5-client-guest-mode-and-end-card`): owns one `GuestFlow` and renders its phase.
 *
 *   - booting / starting: explain session preparation and the security check. The game view
 *     owns actual asset readiness after a session is granted; no invented progress counter;
 *   - queued / full / retry: the guest panels (`GuestScreens.tsx`);
 *   - playing: the normal game view, through `renderGame`, wrapped in guest mode;
 *   - ended: the end card, with "Play again" a fresh start.
 *
 * The Turnstile widget (when the deploy configures a site key) lives in ONE persistent container
 * above every phase, so a challenge that needs a click is on screen whatever phase asked for it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';

import { DEFAULT_GUEST_CTA_TARGETS, createGuestApi } from './guestApi.js';
import type { GuestSession } from './guestApi.js';
import { GuestFlow } from './guestFlow.js';
import { browserStaleClientDeps, shouldReloadForNewBuild } from './staleClient.js';
import type { GuestEndReason, GuestPhase } from './guestFlow.js';
import { GuestModeContext } from './guestContext.js';
import type { GuestModeValue } from './guestContext.js';
import { GuestEndCard, GuestMessageScreen, GuestQueueScreen } from './GuestScreens.js';
import { browserTicker } from './ticker.js';
import { NO_TURNSTILE, createTurnstileVerifier, turnstileSiteKey } from './turnstile.js';
import type { TurnstileVerifier } from './turnstile.js';

export interface GuestGameProps {
  session: GuestSession;
  finalQuestId: string | null;
  onEnd: (reason: GuestEndReason) => void;
}

export function GuestApp({
  renderGame,
  onUnavailable,
}: {
  /** The normal game view for a guest session (App.tsx's `GameScreen` in guest mode). */
  renderGame: (props: GuestGameProps) => JSX.Element;
  /** The demo is not served here (the main origin): fall back to the auth screen + a notice. */
  onUnavailable: () => void;
}): JSX.Element {
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const verifierRef = useRef<TurnstileVerifier>(NO_TURNSTILE);
  const [verifying, setVerifying] = useState(false);
  const [flow] = useState(
    () =>
      new GuestFlow({
        api: createGuestApi(),
        verify: async () => {
          setVerifying(true);
          try {
            return await verifierRef.current.token();
          } finally {
            setVerifying(false);
          }
        },
        ticker: browserTicker(),
        later: (fn, ms) => {
          const handle = window.setTimeout(fn, ms);
          return () => window.clearTimeout(handle);
        },
        // q-live-sword-grip-still-open: never start a session on a client older than the server.
        staleClient: () => shouldReloadForNewBuild(browserStaleClientDeps()),
        reload: () => window.location.reload(),
      }),
  );
  const [phase, setPhase] = useState<GuestPhase>(flow.state);

  useEffect(() => {
    const siteKey = turnstileSiteKey();
    if (siteKey !== null && turnstileRef.current !== null) {
      verifierRef.current = createTurnstileVerifier(turnstileRef.current, siteKey);
    }
    const unsubscribe = flow.subscribe(setPhase);
    void flow.boot();
    return () => {
      unsubscribe();
      flow.dispose();
      verifierRef.current.dispose();
      verifierRef.current = NO_TURNSTILE;
    };
  }, [flow]);

  useEffect(() => {
    if (phase.kind === 'unavailable') onUnavailable();
  }, [phase.kind, onUnavailable]);

  const cfg = flow.config;
  const registrationOpen = cfg?.registrationOpen ?? false;
  const ctaTargets = cfg?.ctaTargets ?? DEFAULT_GUEST_CTA_TARGETS;
  const mode = useMemo<GuestModeValue>(
    () => ({ registrationOpen, ctaTargets, onEnd: (reason) => flow.end(reason) }),
    [registrationOpen, ctaTargets, flow],
  );
  const modeProps = { registrationOpen, ctaTargets };

  let body: JSX.Element | null;
  switch (phase.kind) {
    case 'booting':
    case 'starting':
    case 'unavailable':
      body = (
        <div className="guest-preflight-copy" role="status">
          <h1>{verifying ? 'One check before your adventure' : 'Loading your adventure…'}</h1>
          <p>
            {verifying
              ? 'Complete the security check to start your adventure.'
              : 'Preparing your guest session.'}
          </p>
        </div>
      );
      break;
    case 'queued':
      body = (
        <GuestQueueScreen position={phase.position} etaSeconds={phase.etaSeconds} {...modeProps} />
      );
      break;
    case 'full':
      body = (
        <GuestMessageScreen
          heading="The demo and its waiting line are full."
          message="So many people are trying it right now that there is no room to wait. Come back a little later."
          actionLabel="Try again"
          onAction={() => flow.playAgain()}
          {...modeProps}
        />
      );
      break;
    case 'retry':
      body = (
        <GuestMessageScreen
          heading="The demo didn't start."
          message={phase.message}
          actionLabel={phase.code === 'guestQueueUnknown' ? 'Rejoin the line' : 'Try again'}
          onAction={() => flow.playAgain()}
          {...modeProps}
        />
      );
      break;
    case 'playing':
      body = (
        <GuestModeContext.Provider value={mode}>
          {renderGame({
            session: phase.session,
            finalQuestId: cfg?.finalQuestId ?? null,
            onEnd: (reason) => flow.end(reason),
          })}
        </GuestModeContext.Provider>
      );
      break;
    case 'ended':
      body = (
        <GuestEndCard reason={phase.reason} onPlayAgain={() => flow.playAgain()} {...modeProps} />
      );
      break;
  }

  return (
    <div
      className={`guest-app${['booting', 'starting', 'unavailable'].includes(phase.kind) ? ' guest-preflight' : ''}`}
    >
      {body}
      <div
        ref={turnstileRef}
        className={`guest-turnstile${phase.kind === 'playing' || phase.kind === 'ended' ? ' is-hidden' : ''}`}
        aria-label="Bot check"
      />
    </div>
  );
}
